/**
 * Server-side Cloudflare Turnstile token verification.
 *
 * Call this from API routes to verify that a request came from a
 * real browser, not a bot.
 */

const VERIFY_URL = "https://challenges.cloudflare.com/turnstile/v0/siteverify";

interface TurnstileVerifyResponse {
  success: boolean;
  "error-codes"?: string[];
  challenge_ts?: string;
  hostname?: string;
}

/**
 * Verify a Turnstile token against the Cloudflare API.
 *
 * Returns `true` if the token is valid.
 * Fails CLOSED on errors — blocks requests when verification is unavailable.
 *
 * @param token - The `cf-turnstile-response` token from the client
 * @returns Whether the token is valid
 */
export async function verifyTurnstileToken(token: string): Promise<boolean> {
  const secret = process.env.TURNSTILE_SECRET_KEY;
  if (!secret) {
    console.error("[turnstile] TURNSTILE_SECRET_KEY is not set — blocking request (fail closed)");
    return false;
  }

  try {
    const response = await fetch(VERIFY_URL, {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        secret,
        response: token,
      }),
    });

    if (!response.ok) {
      console.error(`[turnstile] Cloudflare API returned ${response.status} — blocking request`);
      return false;
    }

    const data: TurnstileVerifyResponse = await response.json();

    if (!data.success) {
      // The token's length, never the token: a truncated one shows up short, and
      // Cloudflare documents 2048 characters as the maximum.
      console.warn(
        "[turnstile] Verification failed:",
        data["error-codes"]?.join(", ") ?? "unknown error",
        `(token length ${token.length})`
      );
    }

    return data.success;
  } catch (err) {
    console.error("[turnstile] Verification request failed:", err);
    // Fail closed — block requests when Cloudflare is unreachable
    return false;
  }
}
